import type { RewardsCatalogEntry, RewardsCatalogEntryStatus } from '~/services/RewardsService';

/**
 * Cabin class keys from the fare-family pricing API tier structure.
 * Each tier contains a fareFamilyId and computed pricing data.
 */
export type FareFamilyCabinClass = 'economy' | 'premium_economy' | 'skylounge' | 'lux_row_pax_one' | 'business';

/** Display labels for cabin classes. */
export const CABIN_CLASS_LABELS: Record<FareFamilyCabinClass, string> = {
  economy: 'Economy',
  premium_economy: 'Premium Economy',
  skylounge: 'Economy Extra',
  lux_row_pax_one: 'Economy Extra for One',
  business: 'Business',
};

/** All cabin class keys in display order. */
export const CABIN_CLASSES: Array<FareFamilyCabinClass> = [
  'economy',
  'premium_economy',
  'skylounge',
  'lux_row_pax_one',
  'business',
];

/**
 * Row representation for the pricing DataGrid.
 * Each row represents one flight instance + cabin class combination.
 */
export interface ClassicRewardsPricingRow {
  /** Unique row ID for the DataGrid (journeyKey). */
  id: string;
  /** Reward entry ID from svc-lux-loyalty (null if not yet configured). */
  entryId: string | null;
  /** journeyKey from the charter API, used as itemReference in svc-lux-loyalty. */
  journeyKey: string;
  /** Source flight instance ID from the charter API. */
  flightInstanceId: string;
  /** Fare family ID from the charter API (per cabin class). */
  fareFamilyId: string;
  /** Route display string (e.g. "MEL-MLE"). */
  route: string;
  /** Departure date from the charter API. */
  departureDate: string;
  /** Cabin class display label. */
  cabinClass: string;
  /** Cabin class key for programmatic use. */
  cabinClassKey: FareFamilyCabinClass;
  /** Points price for this reward entry. */
  pointsPrice: number;
  /** Cash gap amount. */
  cashPrice: number;
  /** Currency code (e.g. "AUD"). */
  cashCurrency: string;
  /** Entry status: ACTIVE, INACTIVE, or NOT_CONFIGURED if no entry exists. */
  status: RewardsCatalogEntryStatus | 'NOT_CONFIGURED';
}

/** Search filters for the Classic Rewards page. */
export interface ClassicRewardsSearchFilters {
  departingDepAirport: string;
  departingArrAirport: string;
  returningDepAirport: string;
  returningArrAirport: string;
  cashCurrency: string;
  cabinClasses: Array<FareFamilyCabinClass>;
}

/**
 * Merges fare-family pricing data with rewards entries into grid rows.
 *
 * For each flight instance, creates one row per cabin class. If a rewards entry
 * exists for that journeyKey, it overlays its saved pricing and currency.
 */
export function buildPricingRows(
  fareFamilyResults: Array<{
    flightInstanceId: string;
    depAirport: string;
    arrAirport: string;
    depDate: string;
    tiers: Record<
      FareFamilyCabinClass,
      {
        fareFamilyId: string;
        journeyKey?: string;
      }
    >;
  }>,
  entries: Array<RewardsCatalogEntry>,
  cashCurrency: string,
): Array<ClassicRewardsPricingRow> {
  /** Index entries by journeyKey for O(1) lookup. */
  const entryByJourneyKey = new Map<string, RewardsCatalogEntry>();
  for (const entry of entries) {
    entryByJourneyKey.set(entry.itemReference, entry);
  }

  const rows: Array<ClassicRewardsPricingRow> = [];

  for (const flight of fareFamilyResults) {
    for (const cabinClassKey of CABIN_CLASSES) {
      const tier = flight.tiers[cabinClassKey];
      if (!tier?.fareFamilyId || !tier.journeyKey) {
        continue;
      }

      const { journeyKey } = tier;
      const entry = entryByJourneyKey.get(journeyKey);

      rows.push({
        id: journeyKey,
        entryId: entry?.id ?? null,
        journeyKey,
        flightInstanceId: flight.flightInstanceId,
        fareFamilyId: tier.fareFamilyId,
        route: `${flight.depAirport}-${flight.arrAirport}`,
        departureDate: flight.depDate,
        cabinClass: CABIN_CLASS_LABELS[cabinClassKey],
        cabinClassKey,
        pointsPrice: entry?.pointsPrice ?? 0,
        cashPrice: entry?.cashPrice ?? 0,
        cashCurrency: entry ? entry.cashCurrency : cashCurrency,
        status: entry?.status ?? 'NOT_CONFIGURED',
      });
    }
  }

  return rows;
}
